import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Filter, Loader2 } from "lucide-react";
import { API, api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import { EmptyState, StatCard, TypeBadge, tdClass, thClass } from "../components/Common";
import { ArrowDownRight, ArrowUpRight, Receipt, Wallet, PiggyBank } from "lucide-react";

export default function Laporan() {
  const { hasPerm } = useAuth();
  const [categories, setCategories] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [type, setType] = useState("");
  const [book, setBook] = useState("resmi");
  const [category, setCategory] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type, category, date_from: dateFrom, date_to: dateTo, book });
      const { data } = await api.get(`/reports?${params.toString()}`);
      setReport(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [type, category, dateFrom, dateTo, book]);

  useEffect(() => { load(); }, []);

  const applyMonthYear = (m, y) => {
    if (m && y) {
      const last = new Date(Number(y), Number(m), 0).getDate();
      setDateFrom(`${y}-${m}-01`);
      setDateTo(`${y}-${m}-${String(last).padStart(2, "0")}`);
    } else if (y && !m) {
      setDateFrom(`${y}-01-01`);
      setDateTo(`${y}-12-31`);
    }
  };

  const exportUrl = (fmt) => {
    const params = new URLSearchParams({ format: fmt, type, category, date_from: dateFrom, date_to: dateTo, book });
    return `${API}/export?${params.toString()}`;
  };

  const years = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <div className="space-y-4" data-testid="laporan-page">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 text-slate-100 font-semibold text-sm">
          <Filter className="w-4 h-4" /> Filter Laporan
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <p className="text-xs text-slate-400 mb-1">Dari Tanggal</p>
            <input data-testid="input-report-date-from" type="date" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setMonth(""); setYear(""); }} />
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Sampai Tanggal</p>
            <input data-testid="input-report-date-to" type="date" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setMonth(""); setYear(""); }} />
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Bulan</p>
            <select data-testid="select-report-month" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={month} onChange={(e) => { setMonth(e.target.value); applyMonthYear(e.target.value, year || String(new Date().getFullYear())); if (e.target.value && !year) setYear(String(new Date().getFullYear())); }}>
              <option value="">Semua</option>
              {["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"].map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Tahun</p>
            <select data-testid="select-report-year" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={year} onChange={(e) => { setYear(e.target.value); applyMonthYear(month, e.target.value); }}>
              <option value="">Semua</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Kas</p>
            <select data-testid="select-report-book" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={book} onChange={(e) => setBook(e.target.value)}>
              {hasPerm("view_saldo_resmi") && <option value="resmi">Kas Resmi</option>}
              {hasPerm("view_saldo_imf") && <option value="imf">Kas IMF</option>}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Jenis</p>
            <select data-testid="select-report-type" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Semua</option>
              <option value="income">Pemasukan</option>
              <option value="expense">Pengeluaran</option>
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Kategori</p>
            <select data-testid="select-report-category" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Semua</option>
              {[...new Set(categories.map((c) => c.name))].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button data-testid="btn-apply-report-filter" onClick={load} className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Terapkan
          </button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <StatCard testid="report-opening" title="Saldo Awal Periode" value={formatRupiah(report.opening_balance)} icon={PiggyBank} accent="slate" />
            <StatCard testid="report-income" title="Total Pemasukan" value={formatRupiah(report.total_income)} icon={ArrowDownRight} accent="emerald" />
            <StatCard testid="report-expense" title="Total Pengeluaran" value={formatRupiah(report.total_expense)} icon={ArrowUpRight} accent="rose" />
            <StatCard testid="report-closing" title="Saldo Akhir" value={formatRupiah(report.closing_balance)} icon={Wallet} accent="blue" />
            <StatCard testid="report-count" title="Jumlah Transaksi" value={report.count} icon={Receipt} accent="slate" />
          </div>

          <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-100">Detail Transaksi</h3>
                <p className="text-xs text-slate-400">{report.count} transaksi pada periode terpilih</p>
              </div>
              {hasPerm("export_reports", "export_excel") && (
              <div className="flex gap-2">
                <a data-testid="btn-export-excel" href={exportUrl("xlsx")} className="border border-emerald-500/30 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-100 font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-xs">
                  <FileDown className="w-3.5 h-3.5" /> Excel
                </a>
                <a data-testid="btn-export-csv" href={exportUrl("csv")} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-xs">
                  <FileDown className="w-3.5 h-3.5" /> CSV
                </a>
                <a data-testid="btn-export-pdf" href={exportUrl("pdf")} className="border border-rose-500/30 bg-rose-500/15 text-rose-400 hover:bg-rose-100 font-medium px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-xs">
                  <FileDown className="w-3.5 h-3.5" /> PDF
                </a>
              </div>
              )}
            </div>
            {report.items.length === 0 ? (
              <div className="px-5 pb-8"><EmptyState title="Tidak ada transaksi" description="Tidak ada transaksi yang cocok dengan filter periode ini." /></div>
            ) : (
              <div className="overflow-x-auto max-h-[520px]">
                <table data-testid="table-report-detail" className="w-full border-collapse text-sm text-left">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className={thClass}>No</th>
                      <th className={thClass}>Tanggal</th>
                      <th className={thClass}>Jenis</th>
                      <th className={thClass}>No Bukti</th>
                      <th className={thClass}>Uraian</th>
                      <th className={thClass}>Kategori</th>
                      <th className={`${thClass} text-right`}>Nominal</th>
                      <th className={`${thClass} text-right`}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((t, i) => (
                      <tr key={t.id} className="hover:bg-slate-800/40">
                        <td className={tdClass}>{i + 1}</td>
                        <td className={`${tdClass} whitespace-nowrap`}>{formatDateID(t.date)}</td>
                        <td className={tdClass}><TypeBadge type={t.type} /></td>
                        <td className={`${tdClass} font-num text-xs`}>{t.transaction_number}</td>
                        <td className={`${tdClass} font-medium text-slate-100 max-w-[260px]`}><span className="line-clamp-1">{t.description}</span></td>
                        <td className={tdClass}><span className="bg-slate-800 text-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium">{t.category}</span></td>
                        <td className={`${tdClass} text-right font-num font-semibold ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>{formatRupiah(t.amount)}</td>
                        <td className={`${tdClass} text-right font-num text-blue-300`}>{formatRupiah(t.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
